import { gql, useQuery } from '@apollo/client';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';

// Deadline status of a data subject request, computed by dieter-server
// (src/utils/rightsRequestDeadline.ts) from REQUEST_TIME_OF_ENTRY per Art. 12 Abs. 3 DSGVO.
export type RightsDeadlineStatus = 'OPEN' | 'DUE_SOON' | 'CRITICAL' | 'OVERDUE' | 'DONE' | 'UNKNOWN';

// Single definition of "urgent" for every warning surface (banner, dashboard widget, ...)
// so their counts can never diverge.
export const URGENT_REQUEST_STATUSES: RightsDeadlineStatus[] = ['DUE_SOON', 'CRITICAL', 'OVERDUE'];

export function isUrgentRequest(request: { status: RightsDeadlineStatus }): boolean {
  return URGENT_REQUEST_STATUSES.includes(request.status);
}

export interface RightsRequest {
  index: string;
  receivedAt?: string;
  requestDate?: string;
  typeOfRight?: string;
  deadline?: string;
  daysRemaining?: number;
  status: RightsDeadlineStatus;
  isExtended: boolean;
  completedAt?: string;
  entryQuestionnaireId?: string;
  contractQuestionnaireId?: string;
  extensionQuestionnaireId?: string;
}

export const GET_RIGHTS_REQUESTS = gql`
  query GetRightsRequests($companyId: String!) {
    getRightsRequests(companyId: $companyId) {
      index
      receivedAt
      requestDate
      typeOfRight
      deadline
      daysRemaining
      status
      isExtended
      completedAt
      entryQuestionnaireId
      contractQuestionnaireId
      extensionQuestionnaireId
    }
  }
`;

export interface GetRightsRequestsResult {
  getRightsRequests: RightsRequest[];
}

export interface GetRightsRequestsInput {
  companyId: string;
}

export function useRightsRequestsQuery() {
  const { user } = useUserWithCompany();
  const companyId = user?.company?.id;

  return useQuery<GetRightsRequestsResult, GetRightsRequestsInput>(GET_RIGHTS_REQUESTS, {
    variables: { companyId: companyId || '' },
    skip: !companyId,
    // Deadlines depend on questionnaires filled outside this list (response letter,
    // extension) - re-sync with the server whenever a consumer mounts
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
}
